import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import { acceptBookingComponentReview, executeBookingComponent, fetchBookingById, prepareBookingComponent, requestBookingComponentOtp, updateBookingDetails } from '../../api/Booking';
import '../../css/BookingExecutionPanel.css';

type Traveller = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  passportNumber: string;
  passportExpiry: string;
  pan: string;
};

const emptyTraveller = (): Traveller => ({
  firstName: '', lastName: '', dateOfBirth: '', gender: '', nationality: 'IN', passportNumber: '', passportExpiry: '', pan: '',
});

const passengerCountForOrder = (order: any) => {
  const passengerSnapshot = (order?.line_items || order?.lineItems || [])
    .map((line: any) => line?.snapshot?.passengers)
    .find(Boolean);
  const adults = Number(passengerSnapshot?.adults || 0);
  const children = Number(passengerSnapshot?.children || 0);
  return Number.isFinite(adults + children) && adults + children > 0 ? adults + children : 0;
};

const travellerFormIsComplete = (traveller: Partial<Traveller>) => (
  Boolean(traveller.firstName?.trim()
    && traveller.lastName?.trim()
    && traveller.dateOfBirth
    && traveller.gender
    && traveller.nationality?.trim())
);

const savedSupplierDetailsAreComplete = (order: any, passengerCount: number) => {
  const travellers = order?.travellerSnapshot?.travellers;
  const contact = order?.contactSnapshot;
  return Array.isArray(travellers)
    && travellers.length === passengerCount
    && travellers.every(travellerFormIsComplete)
    && Boolean(contact?.email && contact?.phone);
};

const BookingExecutionPanel: React.FC<{ bookingId: string | number }> = ({ bookingId }) => {
  const auth = useContext(AuthContext) as any;
  const canBook = ['admin', 'ops'].includes(String(auth?.user?.role || '').toLowerCase());
  const [details, setDetails] = useState<any>(null);
  const [travellers, setTravellers] = useState<Traveller[]>([emptyTraveller()]);
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [otp, setOtp] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const order = await fetchBookingById(bookingId);
    const savedTravellers = Array.isArray(order?.travellerSnapshot?.travellers) ? order.travellerSnapshot.travellers : [];
    const passengerCount = passengerCountForOrder(order) || savedTravellers.length || 1;
    setDetails(order);
    setTravellers(Array.from({ length: passengerCount }, (_, index) => ({
      ...emptyTraveller(),
      ...(savedTravellers[index] || {}),
      dateOfBirth: String(savedTravellers[index]?.dateOfBirth || '').slice(0, 10),
      passportExpiry: String(savedTravellers[index]?.passportExpiry || '').slice(0, 10),
    })));
    setContact({ email: order?.contactSnapshot?.email || '', phone: order?.contactSnapshot?.phone || '' });
  }, [bookingId]);

  useEffect(() => {
    void load().catch((error) => toast.error(error.message));
  }, [load]);

  const action = async (line: any, kind: 'prepare' | 'accept' | 'otp' | 'execute') => {
    try {
      setBusy(`${line.id}:${kind}`);
      if (kind === 'prepare') await prepareBookingComponent(bookingId, line.id);
      if (kind === 'accept') await acceptBookingComponentReview(bookingId, line.id);
      if (kind === 'otp') await requestBookingComponentOtp(bookingId, line.id);
      if (kind === 'execute') await executeBookingComponent(bookingId, line.id, otp[line.id] || '');
      await load();
      toast.success(kind === 'otp' ? 'Booking OTP sent to partners@otaprime.com' : 'Component updated');
    } catch (error: any) {
      toast.error(error?.message || 'Booking action failed');
    } finally {
      setBusy('');
    }
  };

  const saveDetails = async () => {
    try {
      setBusy('details');
      await updateBookingDetails(bookingId, { travellers, contact });
      await load();
      toast.success('Supplier booking details saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save booking details');
    } finally {
      setBusy('');
    }
  };

  const passengerCount = useMemo(() => passengerCountForOrder(details) || travellers.length || 1, [details, travellers.length]);
  const supplierDetailsSaved = savedSupplierDetailsAreComplete(details, passengerCount);
  const supplierFormComplete = contact.email.trim() !== '' && contact.phone.trim() !== '' && travellers.length === passengerCount && travellers.every(travellerFormIsComplete);
  const lines = (details?.line_items || []).filter((line: any) => line.componentId);

  if (!details) return <div className="booking-draft-summary__empty">Loading booking execution details…</div>;

  const updateTraveller = (index: number, field: keyof Traveller, value: string) => {
    setTravellers((current) => current.map((traveller, travellerIndex) => (
      travellerIndex === index ? { ...traveller, [field]: value } : traveller
    )));
  };

  return (
    <section className="booking-execution-panel" aria-label="Supplier booking">
      <header className="booking-execution-panel__header">
        <div>
          <h3>Supplier booking</h3>
          <p>Traveller details are required before an automated supplier booking can be prepared.</p>
        </div>
        <span className={`booking-execution-panel__readiness ${supplierDetailsSaved ? 'is-ready' : 'is-pending'}`}>
          {supplierDetailsSaved ? 'Details saved' : 'Details required'}
        </span>
      </header>

      {!canBook && <div className="booking-draft-summary__empty">Only Operations and Administrators can execute supplier bookings.</div>}

      {canBook && (
        <>
          <div className="booking-execution-panel__notice">
            <strong>{passengerCount} traveller{passengerCount === 1 ? '' : 's'} expected.</strong> This list is fixed from the booking’s passenger count.
          </div>

          <div className="booking-execution-panel__form">
            <div className="booking-execution-panel__contact">
              <label>
                Booking contact email
                <input type="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} />
              </label>
              <label>
                Booking contact phone
                <input type="tel" value={contact.phone} onChange={(event) => setContact({ ...contact, phone: event.target.value })} />
              </label>
            </div>

            <div className="booking-execution-panel__travellers">
              {travellers.map((traveller, index) => (
                <fieldset key={index} className="booking-execution-panel__traveller">
                  <legend>Traveller {index + 1}</legend>
                  <div className="booking-execution-panel__fields">
                    <label>First name<input value={traveller.firstName} onChange={(event) => updateTraveller(index, 'firstName', event.target.value)} /></label>
                    <label>Last name<input value={traveller.lastName} onChange={(event) => updateTraveller(index, 'lastName', event.target.value)} /></label>
                    <label>Date of birth<input type="date" value={traveller.dateOfBirth} onChange={(event) => updateTraveller(index, 'dateOfBirth', event.target.value)} /></label>
                    <label>Gender
                      <select value={traveller.gender} onChange={(event) => updateTraveller(index, 'gender', event.target.value)}>
                        <option value="">Select gender</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </label>
                    <label>Nationality<input maxLength={3} value={traveller.nationality} onChange={(event) => updateTraveller(index, 'nationality', event.target.value.toUpperCase())} /></label>
                    <label>Passport number <span>(optional)</span><input value={traveller.passportNumber} onChange={(event) => updateTraveller(index, 'passportNumber', event.target.value)} /></label>
                    <label>Passport expiry <span>(optional)</span><input type="date" value={traveller.passportExpiry} onChange={(event) => updateTraveller(index, 'passportExpiry', event.target.value)} /></label>
                    <label>PAN <span>(optional)</span><input value={traveller.pan} onChange={(event) => updateTraveller(index, 'pan', event.target.value)} /></label>
                  </div>
                </fieldset>
              ))}
            </div>

            <div className="booking-execution-panel__form-actions">
              <p>{supplierDetailsSaved ? 'Saved details are ready for supplier preparation.' : 'Complete and save the required contact and traveller fields to enable preparation.'}</p>
              <button type="button" className="btn" disabled={busy === 'details' || !supplierFormComplete} onClick={() => void saveDetails()}>
                {busy === 'details' ? 'Saving details…' : 'Save supplier details'}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="booking-execution-panel__components">
        {lines.map((line: any) => {
          const prepareDisabled = Boolean(busy) || !supplierDetailsSaved;
          return (
            <article key={line.id} className="booking-execution-panel__component">
              <div className="booking-execution-panel__component-heading">
                <div>
                  <h4>{line.label}</h4>
                  <p>{line.supplier || 'Manual supplier'} · {line.bookingMode === 'AUTOMATED' ? 'Automated booking' : 'Manual booking'}</p>
                </div>
                <span className="booking-execution-panel__status">{line.executionStatus}</span>
              </div>
              {line.executionSnapshot?.quote ? <div className="rfq-muted">Latest supplier quote: {line.executionSnapshot.quote.currency || 'INR'} {line.executionSnapshot.quote.amount}</div> : null}
              {line.failureReason ? <div className="booking-execution-panel__error">{line.failureReason}</div> : null}
              {canBook && line.bookingMode === 'AUTOMATED' && (
                <div className="booking-component-table__actions">
                  {(line.executionStatus === 'PENDING' || line.executionStatus === 'FAILED') && (
                    <button type="button" className="btn-secondary" disabled={prepareDisabled} title={supplierDetailsSaved ? undefined : 'Save complete supplier details before preparing this component'} onClick={() => void action(line, 'prepare')}>
                      {line.executionStatus === 'FAILED' ? 'Re-prepare' : 'Prepare'}
                    </button>
                  )}
                  {line.executionStatus === 'REVIEW_REQUIRED' && <button type="button" className="btn-secondary" disabled={!!busy} onClick={() => void action(line, 'accept')}>Accept revised quote</button>}
                  {line.executionStatus === 'PREPARED' && <button type="button" className="btn-secondary" disabled={!!busy} onClick={() => void action(line, 'otp')}>Request OTP</button>}
                  {line.executionStatus === 'OTP_SENT' && <>
                    <input aria-label={`OTP for ${line.label}`} value={otp[line.id] || ''} maxLength={6} onChange={(event) => setOtp({ ...otp, [line.id]: event.target.value })} />
                    <button type="button" className="btn" disabled={!!busy} onClick={() => void action(line, 'execute')}>Verify &amp; Book</button>
                    <button type="button" className="btn-secondary" disabled={!!busy} onClick={() => void action(line, 'otp')}>Resend OTP</button>
                  </>}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default BookingExecutionPanel;
